import React from "react";
import { useParams, Navigate } from "react-router-dom";
import SEO, { buildFaqJsonLd, buildBreadcrumbJsonLd } from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { ALL_CITY_SLUGS, COUNTIES, slugToCity, toDataSlug } from "@/data/serviceAreas";
import { PRIORITY_CITY_DATA } from "@/data/cityContent";
import { SITE } from "@/lib/site";
import CityHero from "@/components/city/CityHero";
import CityLocalContext from "@/components/city/CityLocalContext";
import CityServicePillars from "@/components/city/CityServicePillars";
import CityQuickLinks from "@/components/city/CityQuickLinks";
import CityNearbyAreas from "@/components/city/CityNearbyAreas";
import CityWhyCallout from "@/components/city/CityWhyCallout";
import CityFAQSection from "@/components/city/CityFAQSection";
import CityContactSidebar from "@/components/city/CityContactSidebar";
import CityWhySection from "@/components/city/CityWhySection";
import { FALLBACK_PROJECTS, buildCityFaqs } from "@/components/city/cityHelpers";

export default function CityDetail() {
  const { slug: urlSlug } = useParams();

  // URL slugs include the -tx suffix; data slugs do not.
  const dataSlug = toDataSlug(urlSlug);

  if (!ALL_CITY_SLUGS.includes(dataSlug)) {
    return <Navigate to="/service-areas" replace />;
  }

  // Canonical URL form is the -tx version. If the visitor (or a stale inbound
  // link) hit the bare slug, redirect them to the canonical -tx URL.
  if (!urlSlug.endsWith("-tx")) {
    return <Navigate to={`/service-areas/${dataSlug}-tx`} replace />;
  }

  const slug = dataSlug; // for the rest of the component, use the data slug
  const cityName = slugToCity(dataSlug);
  const priority = PRIORITY_CITY_DATA[slug];
  const headline = priority?.headline || `${cityName} Construction, Roofing & Home Maintenance`;
  const metaTitle = priority?.metaTitle || `${cityName} TX General Contractor | Black Ridge Construction`;
  const metaDescription = priority?.metaDescription || `Black Ridge Construction serves ${cityName}, TX with roofing, remodeling, additions, exterior repair, and ongoing maintenance plans.`;
  const introCopy = priority?.intro || `Black Ridge Construction is a full-service general contractor working with homeowners and property managers in ${cityName}, TX and the surrounding Central Texas area. We handle roofing, kitchen and bathroom remodels, home additions, outdoor living, exterior repair, investor and rental property work, and ongoing maintenance plans — one trusted team coordinating it all.`;
  const localContext = priority?.localContext || `${cityName} homes face the same Central Texas realities as the rest of our service area: heat, sun, sudden storms, and the kind of wear that adds up if a home does not have a contractor staying ahead of it. We approach every project with practical communication, fair pricing, and the quality of work that builds long-term relationships.`;
  const commonProjects = priority?.commonProjects || FALLBACK_PROJECTS;

  // Find county for related service area links
  const county = COUNTIES.find((c) => c.cities.includes(slug));
  const siblings = (county?.cities || []).filter((s) => s !== slug).slice(0, 8);

  const faqs = buildCityFaqs(cityName);

  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Service Areas", to: "/service-areas" },
    { label: `${cityName}, TX` },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      buildFaqJsonLd(faqs),
      buildBreadcrumbJsonLd(
        breadcrumbs.map((b) => ({
          name: b.label,
          url: b.to ? `${SITE.domain}${b.to}` : `${SITE.domain}/service-areas/${slug}-tx`,
        }))
      ),
    ],
  };

  return (
    <>
      <SEO title={metaTitle} description={metaDescription} jsonLd={jsonLd} />

      <CityHero
        cityName={cityName}
        headline={headline}
        introCopy={introCopy}
        breadcrumbs={breadcrumbs}
      />

      <section className="section-pad bg-br-paper">
        <div className="container-br grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            <CityLocalContext
              cityName={cityName}
              localContext={localContext}
              commonProjects={commonProjects}
            />
            <CityServicePillars cityName={cityName} />
            <CityQuickLinks cityName={cityName} />
            <CityNearbyAreas siblings={siblings} />
            <CityWhyCallout cityName={cityName} />
            <CityFAQSection cityName={cityName} faqs={faqs} />
          </div>
          <CityContactSidebar cityName={cityName} slug={slug} />
        </div>
      </section>

      <CityWhySection cityName={cityName} />

      <CTASection
        title={`Ready to Start Your ${cityName} Project?`}
        subtitle={`We'll review your property, give honest options, and confirm next steps. ${SITE.phone}.`}
      />
    </>
  );
}
